import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { EPLogo } from "../components/shared.jsx";

// /continue?t=<token> — the email confirmation link lands here. We POST the token
// to /v1/onboard/verify, which spends one of its uses and sets the ep_session
// cookie. On success App decides which step to resume at, from gate status.
//
// On failure we distinguish a REJECTED token (expired, spent, bad) from a server
// we could not reach — they need different sentences and different next steps,
// and conflating them is what sent us hunting a token while the backend was down.
//
// POST (not GET) so an email-client link prefetch can't spend a use — that only
// happens from this explicit JS call.
export default function Continue() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("verifying"); // verifying | ok | error
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard — verify exactly once
    ran.current = true;
    const t = params.get("t");
    if (!t) { setState("error"); return; }
    (async () => {
      try {
        await api.post("/v1/onboard/verify", { t });
        // Deliberately does NOT set the wizard's position any more.
        //
        // It used to write ep_onb_step from the verify response, which was a
        // hardcoded 1 — so following a link always landed on Your Profile, even
        // for an agent who had signed their ICA, and even on the browser where
        // they had got further. Worse, writing it at all meant App could no
        // longer tell a browser that knew where the agent was from one that did
        // not, which is the question its resume effect turns on.
        //
        // App decides position now, from /v1/onboarding-gate/status. It is the
        // only place with both the real state and the dynamic step list.
        setState("ok");
        setTimeout(() => navigate("/", { replace: true }), 600);
      } catch (err) {
        // Before showing anything, check if a valid session already exists.
        // This handles: token spent, but a prior attempt succeeded and the
        // session cookie is still good.
        try {
          await api.get("/v1/me");
          navigate("/", { replace: true });
          return;
        } catch { /* genuinely not signed in — work out why below */ }

        // TELL THE TRUTH ABOUT WHICH FAILURE THIS IS.
        //
        // Every failure used to render as "this link may have expired or already
        // been used", including a server that never answered. That sends somebody
        // to request a fresh link, which fails identically, and it sent US looking
        // at the token when the backend was simply down. The token's `attempts`
        // stayed 0 throughout, because the request never reached the row.
        //
        // A rejected token is a 4xx WITH a code from the API. Anything else — a
        // network error (fetch throws a TypeError, not an ApiError), a 5xx, a
        // proxy or gateway failure — means we could not ask the question, which
        // is a different sentence and a different next step.
        const rejected = err instanceof ApiError && err.status >= 400 && err.status < 500;
        setState(rejected ? "error" : "unreachable");
      }
    })();
  }, [params, navigate]);

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--ep-saguaro-blossom)", fontFamily: "var(--ep-font-body)" }}>
      <div style={{ textAlign: "center", maxWidth: 460, padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}><EPLogo height={26} dark /></div>
        {state === "verifying" && (
          <>
            <h1 style={{ font: "300 34px/1.1 var(--ep-font-display)", margin: "0 0 10px" }}>Confirming it's you…</h1>
            <p style={{ color: "var(--ep-fg-muted)" }}>One moment while we verify your secure link.</p>
          </>
        )}
        {state === "ok" && (
          <>
            <h1 style={{ font: "300 34px/1.1 var(--ep-font-display)", margin: "0 0 10px" }}>You're in.</h1>
            <p style={{ color: "var(--ep-fg-muted)" }}>Taking you to your onboarding…</p>
          </>
        )}
        {state === "error" && (
          <>
            <h1 style={{ font: "300 34px/1.1 var(--ep-font-display)", margin: "0 0 10px" }}>This link can't be used.</h1>
            <p style={{ color: "var(--ep-fg-muted)", marginBottom: 24 }}>
              {/* "7 days" mirrors TOKEN_TTL_MIN and "5 times" mirrors
                  LINK_MAX_USES, both in backend/routes/onboard.js. The browser
                  cannot read either, so they are pinned together by
                  onboarding-link-ttl.test.js rather than by hope. */}
              It has expired or been used up. Links are valid for 7 days and can be opened
              5 times. Request a fresh one to continue.
            </p>
            <Link to="/" className="ep-btn primary" style={{ textDecoration: "none" }}>Back to Find You</Link>
          </>
        )}
        {state === "unreachable" && (
          <>
            {/* A DIFFERENT failure and a different instruction. Nothing is wrong
                with their link, so "request a fresh one" would be bad advice —
                the new one would fail in exactly the same way. */}
            <h1 style={{ font: "300 34px/1.1 var(--ep-font-display)", margin: "0 0 10px" }}>We couldn't reach EveryPlace.</h1>
            <p style={{ color: "var(--ep-fg-muted)", marginBottom: 24 }}>
              Your link is fine — we just couldn't get a reply from our end. Check your
              connection and open the same link again in a moment. If it keeps happening,
              email <a href="mailto:onboard@everyplace.com" style={{ color: "var(--ep-sunset)" }}>onboard@everyplace.com</a>.
            </p>
            <button className="ep-btn primary" onClick={() => window.location.reload()}>Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
